/**
 * Push Notifications Service
 * Handles FCM token management and device registration with the backend
 */

import { getFCMToken, isFirebaseConfigured, getMessagingInstance, getToken } from './firebase';
import { request, STORAGE_KEYS } from './bonndleApi';

const FCM_TOKEN_KEY = 'bonndle:fcm_token';

/**
 * Detect the current platform
 * @returns {'web' | 'ios' | 'android' | 'mobile'}
 */
function detectPlatform() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // Check if running as installed PWA
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;

  // Detect iOS
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'ios';
  }

  // Detect Android
  if (/android/i.test(userAgent)) {
    return 'android';
  }

  // If it's a standalone PWA on desktop, still return 'web'
  return 'web';
}

/**
 * Get stored FCM token
 */
function getStoredFCMToken() {
  return localStorage.getItem(FCM_TOKEN_KEY);
}

/**
 * Store FCM token locally
 */
function storeFCMToken(token) {
  if (token) {
    localStorage.setItem(FCM_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(FCM_TOKEN_KEY);
  }
}

/**
 * Register device token with the backend
 * POST /users/:uuid/devices
 * @param {string} token - FCM token
 * @param {string} [platform] - Platform type (web, ios, android, mobile)
 */
async function registerDeviceWithBackend(token, platform) {
  const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
  if (!userId) {
    throw new Error('No user ID found. User must be logged in to register device.');
  }

  const payload = {
    token,
    platform: platform || detectPlatform(),
  };

  return request(`/users/${userId}/devices`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Register device for push notifications
 * Call this after login and whenever the FCM token is refreshed
 * @returns {Promise<{success: boolean, token?: string, error?: string}>}
 */
export async function registerDevice() {
  if (!isFirebaseConfigured()) {
    console.log('Firebase not configured, skipping device registration');
    return { success: false, error: 'Firebase not configured' };
  }

  try {
    const token = await getFCMToken();
    if (!token) {
      return { success: false, error: 'Could not get FCM token' };
    }

    // Check if token changed
    const storedToken = getStoredFCMToken();
    const tokenChanged = storedToken !== token;

    // Register with backend
    await registerDeviceWithBackend(token);

    // Store token locally
    storeFCMToken(token);

    console.log('Device registered successfully', { tokenChanged });
    return { success: true, token };
  } catch (error) {
    console.error('Failed to register device:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Set up FCM token refresh listener
 * Automatically re-registers device when token changes
 */
export async function setupTokenRefreshListener() {
  if (!isFirebaseConfigured()) {
    return () => {};
  }

  const messagingInstance = await getMessagingInstance();
  if (!messagingInstance) {
    return () => {};
  }

  // Firebase SDK v9+ handles token refresh via getToken()
  // We'll set up a periodic check and also listen for service worker messages
  const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

  // Check for token refresh periodically (every 30 minutes)
  const checkInterval = setInterval(async () => {
    try {
      const currentToken = await getToken(messagingInstance, { vapidKey: VAPID_KEY });
      const storedToken = getStoredFCMToken();

      if (currentToken && currentToken !== storedToken) {
        console.log('FCM token refreshed, re-registering device');
        await registerDevice();
      }
    } catch (error) {
      console.warn('Token refresh check failed:', error);
    }
  }, 30 * 60 * 1000); // 30 minutes

  return () => {
    clearInterval(checkInterval);
  };
}

/**
 * Clear device registration data on logout
 */
export function clearDeviceRegistration() {
  localStorage.removeItem(FCM_TOKEN_KEY);
}

export default {
  registerDevice,
  setupTokenRefreshListener,
  clearDeviceRegistration,
};
